import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayISO = toISODate(new Date());

// A date is blocked if it falls inside [checkIn, checkOut) of any confirmed
// booking - the checkout day itself is a valid same-day-turnover check-in.
const isBlocked = (iso, blockedRanges) =>
  blockedRanges.some((r) => iso >= r.checkInDate && iso < r.checkOutDate);

const rangeCrossesBlocked = (startIso, endIso, blockedRanges) =>
  blockedRanges.some((r) => startIso < r.checkOutDate && r.checkInDate < endIso);

/**
 * Small custom date-range calendar (no external date-picker dependency,
 * given the requirement is a single always-simple case: one room, one
 * contiguous range, blocked dates greyed out). Click once for check-in,
 * click again for check-out; clicking before check-in or across a blocked
 * range restarts the selection.
 */
export default function BookingCalendar({ blockedRanges = [], checkIn, checkOut, onChange }) {
  const [viewMonth, setViewMonth] = useState(() => {
    const d = checkIn ? new Date(checkIn) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const handleDayClick = (iso) => {
    if (!checkIn || checkOut) {
      onChange(iso, null);
      return;
    }
    if (iso <= checkIn) {
      onChange(iso, null);
      return;
    }
    if (rangeCrossesBlocked(checkIn, iso, blockedRanges)) {
      onChange(iso, null);
      return;
    }
    onChange(checkIn, iso);
  };

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = firstOfMonth.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(year, month, day));

  const monthLabel = viewMonth.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 select-none">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setViewMonth(new Date(year, month - 1, 1))}
          className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="font-semibold text-gray-800">{monthLabel}</span>
        <button
          type="button"
          onClick={() => setViewMonth(new Date(year, month + 1, 1))}
          className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-xs font-semibold text-gray-400 py-1">
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((date, i) => {
          if (!date) return <div key={`blank-${i}`} />;

          const iso = toISODate(date);
          const past = iso < todayISO;
          const blocked = isBlocked(iso, blockedRanges);
          const disabled = past || blocked;
          const isCheckIn = iso === checkIn;
          const isCheckOut = iso === checkOut;
          const inRange = checkIn && checkOut && iso > checkIn && iso < checkOut;

          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => handleDayClick(iso)}
              title={blocked ? "Already booked" : undefined}
              className={`aspect-square rounded-lg text-sm font-medium transition-colors ${
                disabled
                  ? "text-gray-300 line-through cursor-not-allowed"
                  : isCheckIn || isCheckOut
                  ? "bg-emerald-600 text-white"
                  : inRange
                  ? "bg-emerald-100 text-emerald-800"
                  : "text-gray-700 hover:bg-emerald-50"
              }`}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-emerald-600 inline-block" /> Selected
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-gray-200 inline-block" /> Booked / past
        </span>
      </div>
    </div>
  );
}
